import '../styles/index.css';
import { useRef, useEffect } from 'react';
import type { PresentationEvent } from '../../types/presentation.js';
import { ChatBubble } from './ChatBubble.js';
import { NarrationBox } from './NarrationBox.js';
import { SystemMessage } from './SystemMessage.js';
import { TimePassDisplay } from './TimePassDisplay.js';
import { DateChangeDisplay } from './DateChangeDisplay.js';
import { SpeakerIntro } from './SpeakerIntro.js';

export interface ChatListProps {
  /** Array of presentation events to display */
  events: PresentationEvent[];
  /** Whether to auto-scroll to bottom when new events are added */
  autoScroll?: boolean;
  /** Custom class name */
  className?: string;
}

export function ChatList({
  events,
  autoScroll = true,
  className,
}: ChatListProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const prevLengthRef = useRef(events.length);

  // Auto-scroll when new events are added (with smooth behavior)
  useEffect(() => {
    if (autoScroll && events.length > prevLengthRef.current && listRef.current) {
      listRef.current.scrollTo({
        top: listRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
    prevLengthRef.current = events.length;
  }, [events.length, autoScroll]);

  const containerClass = ['pawa-chat-list', className].filter(Boolean).join(' ');

  return (
    <div ref={listRef} className={containerClass}>
      {events.map((event, index) => (
        <ChatListItem key={index} event={event} />
      ))}
    </div>
  );
}

function ChatListItem({ event }: { event: PresentationEvent }) {
  switch (event.type) {
    case 'say':
      return (
        <ChatBubble
          speaker={event.speaker}
          displayName={event.displayName}
          text={event.text}
          avatarUrl={event.avatarUrl}
          side={event.chatSide || 'left'}
          variant="say"
        />
      );

    case 'think':
      return (
        <ChatBubble
          speaker={event.speaker || 'narrator'}
          displayName={event.displayName || 'Thoughts'}
          text={event.text}
          avatarUrl={event.avatarUrl}
          side={event.chatSide || 'left'}
          variant="think"
        />
      );

    case 'narrate':
      return <NarrationBox text={event.text} />;

    case 'system':
      return <SystemMessage text={event.text} />;

    case 'time_pass':
      return <TimePassDisplay description={event.description} />;

    case 'date_change':
      return <DateChangeDisplay date={event.date} />;

    case 'speaker_intro':
      return (
        <SpeakerIntro
          speakerId={event.speakerId}
          displayName={event.displayName}
        />
      );

    case 'background':
      // Show as a subtle notification in the chat
      return (
        <div className="pawa-bg-change-container pawa-chat-item">
          <div className="pawa-bg-change-badge">
            <span>🖼️</span>
            <span>Scene changed</span>
          </div>
        </div>
      );

    case 'transition':
      // Transitions don't render inline
      return null;

    case 'minigame':
      return (
        <div className="pawa-system-container pawa-chat-item">
          <div className="pawa-system-message">
            🎮 Minigame: {event.minigameId}
          </div>
        </div>
      );

    default:
      return null;
  }
}
